import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { fmtTime, songGainAt, fadeToBlack, clipSpeed, clipSpeedMap, speedAtSource, srcToTimeline, timelineToSrc, clipFadeIn, clipFadeOut } from '../utils/movie';
import { httpsify } from '../utils/url';
import { UploadTrack, YouTubeTrack } from './AudioTracks';

// Gated diagnostics (same flag as the song tracks): set
// `window.__CS_AUDIO_DEBUG = true` to trace clip/video-side events, so audible
// artifacts can be attributed to song lanes vs clip audio vs buffering.
const adbg = (...args) => {
  if (typeof window !== 'undefined' && window.__CS_AUDIO_DEBUG) {
    // eslint-disable-next-line no-console
    console.debug('[player]', ...args);
  }
};

// Edit proxies up to this size are fully prefetched into a local blob for
// instant scrubbing; bigger ones are range-streamed from the URL (only the
// played region is fetched). ~120 MB ≈ 3+ minutes of the 1080p edit proxy
// (~4-6 Mbps) — sized so typical surf clips still get the zero-buffer blob
// path after the 540p→1080p proxy bump.
const EDIT_BLOB_MAX_BYTES = 120 * 1024 * 1024;

// Touch devices (phones/tablets) skip the blob pipeline entirely: sequentially
// downloading every proxy before exposing ANY playable URL means minutes of
// dead play button on a cell connection, and 100+ MB blobs can OOM the mobile
// Safari tab. They range-stream the MP4s directly instead — instant start,
// only the played region fetched. Desktop keeps the zero-buffer blob path.
const IS_TOUCH = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

// True where the <video> element plays .m3u8 itself (Safari/iOS) — those
// sessions never construct hls.js, so quality control goes through the
// native-variant picker instead of the hls.js level API.
const NATIVE_HLS = typeof document !== 'undefined'
  && !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');

// Memory cap for the *next-clip* pre-buffer blobs (the large proxies we fetch
// ahead so transitions don't stall). A byte-capped LRU evicts the oldest, never
// the current/next clip. Raised with the blob gate (1080p proxies are ~3x the
// old 540p size).
const PREFETCH_CACHE_MAX_BYTES = 480 * 1024 * 1024;

// Plays a movie composed of ordered clips (stitched client-side) plus a
// sequenced, mixable soundtrack with fades / crossfades and movie-level
// fade-to-black intro/outro.
export default function CompositionPlayer({
  clips = [],
  songs = [],
  hasOriginalAudio = true,
  intro = 0,
  outro = 0,
  poster,
  onMovieDuration,
  onTime,
  seekRef,
  ctlRef,
  onPlayingChange,
  editing = false,
  initialTime = 0,
  initialPlaying = false,
}) {
  // Double-buffered playback: TWO stacked <video> elements. The ACTIVE one
  // plays the current clip and drives the master clock; the STANDBY one
  // preloads the NEXT clip (src set, seeked to its in-point, buffering while
  // the current clip plays) so a clip boundary becomes a same-frame element
  // swap instead of a src reload — no network/decode latency at the cut, no
  // visual blip, and the audio clock's bridge window shrinks to ~nothing.
  const vidElsRef = useRef([null, null]);
  const hlsRefs = useRef([null, null]); // per-element hls.js instance (or null)
  // What each element currently holds: { segId, url }, set once metadata is
  // loaded and the element is positioned. The boundary fast-swap requires the
  // standby's marker to match the upcoming clip.
  const elPrepRef = useRef([null, null]);
  // Autoplay-policy priming: whether each element has been .play()ed inside a
  // user gesture (Safari/iOS gate programmatic play() per-element).
  const blessedRef = useRef([false, false]);
  const [activeIdx, setActiveIdx] = useState(0);
  const activeIdxRef = useRef(0);
  // Which element is SHOWN. On a swap it lags activeIdx until the incoming
  // element has painted its first frame, so the cut never flashes black.
  const [visibleIdx, setVisibleIdx] = useState(0);
  // Fully-downloaded edit proxies, keyed by source URL -> blob object URL. A
  // small progressive MP4 played from a local blob has zero network during
  // playback, so scrubbing/high-speed playback never buffers.
  const blobCacheRef = useRef(new Map());
  // Bookkeeping for the next-clip pre-buffer: which big proxies we've fetched
  // ahead (src -> {bytes, clipId}), their LRU order, and any in-flight fetches.
  const blobInfoRef = useRef(new Map());
  const blobLruRef = useRef([]);
  const prefetchingRef = useRef(new Set());
  const rootRef = useRef(null);
  const clockRef = useRef({ time: Math.max(0, initialTime || 0), playing: false, master: 1, mode: 'composed' });
  const [isFs, setIsFs] = useState(false);
  // True while the iOS NATIVE video fullscreen is up (see the fullscreen
  // section) — the boundary fast-swap is suppressed for its duration.
  const nativeFsRef = useRef(false);

  const [urls, setUrls] = useState({});
  const [measured, setMeasured] = useState({});
  // Tab switches remount the player; start on the clip that holds the
  // carried-over playhead (initialTime) instead of always at clip 0. The
  // clip-load handler then lands the video at the clock position (seeded
  // below via lastTimeRef), so no explicit seek is needed.
  const [segIndex, setSegIndex] = useState(() => {
    const t0 = Math.max(0, initialTime || 0);
    if (!t0) return 0;
    let acc = 0;
    for (let i = 0; i < clips.length; i += 1) {
      const c = clips[i];
      const out = c.outSec && c.outSec > 0 ? c.outSec : c.sourceDurationSec || 0;
      const len = clipSpeedMap({ ...c, outSec: out }).tlLen;
      if (t0 < acc + len) return i;
      acc += len;
    }
    // Unknown durations (not measured yet) — start at 0 rather than guessing.
    return acc > 0 ? Math.max(0, clips.length - 1) : 0;
  });
  // Seeded from the carried-over transport state (tab switches remount the
  // player): stay paused if the previous tab was paused, resume if playing.
  const [playing, setPlaying] = useState(!!initialPlaying);
  // Seed the displayed playhead from the carried-over time (tab switches
  // remount the player; see the initial-seek effect below).
  const [movieTime, setMovieTime] = useState(Math.max(0, initialTime || 0));
  const [black, setBlack] = useState(0);
  // Per-clip effect overlay: clip fade-in/out to black/white + the approximate
  // cross-dissolve "fade-through" (see the clock loop). Separate from the
  // movie-level fade-to-black overlay so their colors can differ.
  const [fx, setFx] = useState({ color: '#000', opacity: 0 });

  // Buffering + adaptive-quality UI.
  const [buffering, setBuffering] = useState(false);
  const [bufferPct, setBufferPct] = useState(0);
  const [bufferEta, setBufferEta] = useState(null); // seconds to target, or null
  const [levels, setLevels] = useState([]); // hls renditions, if any
  const [activeLevel, setActiveLevel] = useState(-1); // -1 = auto/native
  const [pickedLevel, setPickedLevel] = useState(-1); // -1 = Auto
  const [qualityOpen, setQualityOpen] = useState(false);
  // Native-HLS (Safari) quality control: no hls.js = no level API, so the
  // variant playlists parsed from the master act as the menu; picking one
  // plays that rung's index.m3u8 DIRECTLY (Safari buffers a fixed rung far
  // more predictably than its opaque ABR, which is exactly what a viewer on a
  // thin connection needs). 0 = Auto (master playlist).
  const [nativeVariants, setNativeVariants] = useState([]); // [{height, url}]
  const [nativePick, setNativePick] = useState(0); // 0 = Auto, else rung height
  const nativeVariantsRef = useRef({ master: '', list: [] });

  const playingRef = useRef(!!initialPlaying);
  // One-shot: true while the FIRST clip load's auto-resume (carried play state)
  // is pending — its play() rejection falls back to a paused UI instead of a
  // "playing" transport over a frozen frame (browser autoplay policy guard).
  const initialResumeRef = useRef(!!initialPlaying);
  const segRef = useRef(segIndex);
  // Movie-clock continuity across clip cuts. `activeSegRef` is the clip the
  // <video> has actually loaded + seeked (set on loadedmetadata); until it
  // catches up to segRef after an advance/seek, the clock must NOT derive time
  // from a mismatched (segment, video.currentTime) pair or the soundtrack jumps
  // backward and replays. `lastTime/lastTs` carry the clock forward by wall-clock
  // through the brief load gap so music plays smoothly through the cut.
  const activeSegRef = useRef(null);
  // Seeded with the carried-over playhead so the first clip load (the onMeta
  // handler derives its landing spot from this clock) starts there, not at 0.
  const lastTimeRef = useRef(Math.max(0, initialTime || 0));
  const lastTsRef = useRef(0);
  // `buffering` gates the VIDEO almost instantly (the gate UI), but the
  // soundtrack only pauses on a genuine stall — the sub-second seek/load blip
  // at every clip switch must NOT pause/resume the song elements (audible
  // click). `stalledRef` is the debounced flag the clock hands to the songs.
  const stalledRef = useRef(false);
  // Bumped on every explicit user seek so song elements resync instantly
  // (instead of waiting out the sustained-drift check meant for glitches).
  const seekEpochRef = useRef(0);
  // Last applied clip-audio gain — only for the debug log that flags sudden
  // one-frame jumps (helps attribute audible artifacts to clip audio).
  const lastOrigGainRef = useRef(0);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);
  // Debounce buffering -> stalled: micro-gaps (clip-switch seek/load, usually
  // well under 400ms with the pre-buffer) keep the music rolling on the
  // wall-clock bridge; only a persistent stall pauses the soundtrack.
  useEffect(() => {
    if (!buffering) {
      stalledRef.current = false;
      return undefined;
    }
    const t = setTimeout(() => {
      stalledRef.current = true;
      adbg('STALL: buffering >400ms — soundtrack clock pausing');
    }, 400);
    return () => clearTimeout(t);
  }, [buffering]);
  useEffect(() => {
    segRef.current = segIndex;
  }, [segIndex]);
  useEffect(() => {
    activeIdxRef.current = activeIdx;
  }, [activeIdx]);

  const segments = useMemo(() => {
    let t = 0;
    return clips.map((c) => {
      const out = c.outSec && c.outSec > 0 ? c.outSec : measured[c.id] || c.sourceDurationSec || 0;
      const speed = clipSpeed(c);
      const srcLen = Math.max(0, (out || 0) - (c.inSec || 0));
      // Speed map honors per-clip speed ramps (variable speed over time). For a
      // fixed-speed clip `len` is just srcLen/speed; for a ramp it integrates.
      const smap = clipSpeedMap({ ...c, outSec: out });
      const len = smap.tlLen; // movie-timeline length after speed (ramp-aware)
      // SDK preview proxies (clip.sdkPreview, merged in by EditMovie) are
      // clip-WINDOW files: the file's t=0 is source second startSec, so every
      // video.currentTime read/write shifts by srcOff. Editing mode only —
      // "Full quality" deliberately keeps the UHD delivery source.
      const srcOff = editing && c.sdkPreview && c.sdkPreview.url ? c.sdkPreview.startSec || 0 : 0;
      const seg = { ...c, outEff: out, speed, srcLen, len, smap, maxSpeed: smap.maxSpeed, startInMovie: t, srcOff };
      t += len;
      return seg;
    });
  }, [clips, measured, editing]);

  const totalDur = useMemo(() => segments.reduce((s, c) => s + c.len, 0), [segments]);
  useEffect(() => {
    if (onMovieDuration) onMovieDuration(totalDur);
  }, [totalDur, onMovieDuration]);

  const hasSongs = songs.length > 0;
  const [mode, setMode] = useState(hasSongs ? 'composed' : 'original');
  useEffect(() => setMode(hasSongs ? 'composed' : 'original'), [hasSongs]);

  const uploadSongs = useMemo(
    () => songs.filter((s) => s.source === 'upload' && s.url),
    [songs]
  );
  const ytSongs = useMemo(
    () => songs.filter((s) => s.source === 'youtube' && s.videoId),
    [songs]
  );

  // Resolve playback URLs (CDN url, else presign raw key).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      const blobCache = blobCacheRef.current;
      for (const c of clips) {
        // While editing, play the lightweight low-res edit proxy when present —
        // far less buffering on scrub. Prefer the single-file MP4 (fully
        // prefetched below) over the segmented HLS proxy; fall back to the full
        // proxy/original. Render/publish (editing=false) always use `url`.
        // A valid SDK preview proxy outranks the ordinary edit proxies while
        // editing: the RED clip then previews its TRUE bake de-log (it's a
        // window MP4 — the segments' srcOff carries the time shift).
        const editSrc = editing
          ? (c.sdkPreview && c.sdkPreview.url) || c.editMp4Url || c.editUrl
          : '';
        const src = editSrc || c.url;
        if (src) {
          const httpsSrc = httpsify(src);
          const isMp4Edit = !IS_TOUCH && editing && editSrc && !/\.m3u8(\?|$)/.test(httpsSrc);
          if (isMp4Edit) {
            if (blobCache.has(httpsSrc)) next[c.id] = blobCache.get(httpsSrc);
            else {
              // Size-gate: small proxies (short clips) are fully downloaded into a
              // local blob for instant, zero-network scrubbing. Large proxies
              // (long source clips, e.g. a 26-min stream) are played from the URL
              // directly so the browser range-requests only the part being played
              // — much lighter than buffering the whole file, and no HLS segment
              // stalls. Either way it's a single progressive MP4 (smooth seek).
              let lenBytes = 0;
              try {
                // eslint-disable-next-line no-await-in-loop
                const head = await fetch(httpsSrc, { method: 'HEAD' });
                lenBytes = Number(head.headers.get('content-length') || 0);
              } catch { lenBytes = 0; }
              if (lenBytes > 0 && lenBytes <= EDIT_BLOB_MAX_BYTES) {
                try {
                  // eslint-disable-next-line no-await-in-loop
                  const resp = await fetch(httpsSrc);
                  // eslint-disable-next-line no-await-in-loop
                  const blob = await resp.blob();
                  if (cancelled) break;
                  const burl = URL.createObjectURL(blob);
                  blobCache.set(httpsSrc, burl);
                  next[c.id] = burl;
                } catch {
                  next[c.id] = httpsSrc; // fall back to range-streaming the MP4
                }
              } else {
                next[c.id] = httpsSrc; // large file: range-stream the MP4
              }
            }
          } else {
            next[c.id] = httpsSrc;
          }
        } else if (c.s3Key) {
          try {
            // eslint-disable-next-line no-await-in-loop
            next[c.id] = await getAssetPlaybackUrl(c.s3Key);
          } catch {
            next[c.id] = '';
          }
        }
      }
      if (!cancelled) setUrls(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, clips.map((c) => c.id + (editing ? (c.sdkPreview?.url || '') + (c.editMp4Url || c.editUrl || c.url || c.s3Key || '') : c.url || c.s3Key || '')).join('|')]);

  // Release media resources on unmount: destroy both hls instances, detach
  // both video elements' sources, and revoke any blob URLs.
  useEffect(() => {
    const cache = blobCacheRef.current;
    const els = vidElsRef.current;
    const hlses = hlsRefs.current;
    return () => {
      for (let i = 0; i < hlses.length; i += 1) {
        try { if (hlses[i]) hlses[i].destroy(); } catch { /* noop */ }
        hlses[i] = null;
      }
      for (const el of els) {
        if (!el) continue;
        try {
          el.pause();
          el.removeAttribute('src');
          el.load();
        } catch { /* noop */ }
      }
      for (const u of cache.values()) {
        try { URL.revokeObjectURL(u); } catch { /* noop */ }
      }
      cache.clear();
    };
  }, []);

  // Pre-buffer the NEXT clip so the cut to it never stalls. The resolution
  // effect already fully downloads *small* edit proxies up front; the gap is the
  // big ones (long sources) that are range-streamed — switching to one mid-
  // playback forces a fresh buffer. Here we fetch the upcoming clip's MP4 proxy
  // into a local blob ahead of time (any size) and upgrade its URL, so the
  // transition plays from memory with no network. A byte-capped LRU bounds
  // memory; the current + next clips are never evicted. Editing only.
  //
  // CRITICAL: this must never steal bandwidth from the *current* clip, or the
  // current clip buffers forever. So we only prefetch once the current clip is
  // actually playing and NOT buffering, wait a beat to make sure it's healthy,
  // and abort the prefetch immediately if the current clip starts buffering
  // (the AbortController + the !buffering gate). It resumes when healthy again.
  useEffect(() => {
    if (IS_TOUCH || !editing || !playing || buffering) return undefined;
    const nseg = segments[segIndex + 1];
    if (!nseg) return undefined;
    const editSrc = nseg.editMp4Url || nseg.editUrl;
    if (!editSrc) return undefined;
    const httpsSrc = httpsify(editSrc);
    if (/\.m3u8(\?|$)/.test(httpsSrc)) return undefined; // progressive MP4 only
    const cache = blobCacheRef.current;
    if (cache.has(httpsSrc) || prefetchingRef.current.has(httpsSrc)) return undefined;

    let cancelled = false;
    const ctrl = new AbortController();
    // Hold off ~1.5s so a freshly-resumed clip fills its own buffer first.
    const timer = setTimeout(() => {
      prefetchingRef.current.add(httpsSrc);
      (async () => {
        try {
          const resp = await fetch(httpsSrc, { signal: ctrl.signal });
          const blob = await resp.blob();
          if (cancelled) return;
          const burl = URL.createObjectURL(blob);
          cache.set(httpsSrc, burl);
          blobInfoRef.current.set(httpsSrc, { bytes: blob.size, clipId: nseg.id });
          const lru = blobLruRef.current;
          const k = lru.indexOf(httpsSrc);
          if (k >= 0) lru.splice(k, 1);
          lru.push(httpsSrc);
          // Swap the upcoming clip onto its local blob so the cut is instant.
          setUrls((u) => (u[nseg.id] === burl ? u : { ...u, [nseg.id]: burl }));
          // Evict oldest pre-buffered blobs over the cap (keep current + next).
          const protectedIds = new Set([segments[segRef.current]?.id, nseg.id]);
          const info = blobInfoRef.current;
          let total = 0;
          info.forEach((v) => { total += v.bytes; });
          let i = 0;
          while (total > PREFETCH_CACHE_MAX_BYTES && i < lru.length) {
            const src = lru[i];
            const meta = info.get(src);
            if (!meta || protectedIds.has(meta.clipId)) { i += 1; continue; }
            const old = cache.get(src);
            try { URL.revokeObjectURL(old); } catch { /* noop */ }
            cache.delete(src);
            info.delete(src);
            lru.splice(i, 1);
            total -= meta.bytes;
            setUrls((u) => (u[meta.clipId] === old ? { ...u, [meta.clipId]: src } : u));
          }
        } catch {
          /* aborted (current clip needed bandwidth) or failed: keep range-stream */
        } finally {
          prefetchingRef.current.delete(httpsSrc);
        }
      })();
    }, 1500);
    return () => { cancelled = true; clearTimeout(timer); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, segIndex, segments, playing, buffering]);

  const curSeg = segments[segIndex];
  const curUrl = curSeg ? urls[curSeg.id] : '';

  // Fetch + parse the HLS master playlist's variant list for the NATIVE
  // player's quality menu (Safari/iOS take the native branch below, which has
  // no level API). Tiny playlist fetch, cached per master URL.
  useEffect(() => {
    const url = curUrl;
    const isHls = curSeg && (curSeg.streamType === 'hls' || /\.m3u8(\?|$)/.test(url || ''));
    const isLocal = (url || '').startsWith('blob:') || /\.mp4(\?|$)/.test(url || '');
    const wantNative = isHls && !isLocal && NATIVE_HLS;
    if (!wantNative) { setNativeVariants([]); return undefined; }
    if (nativeVariantsRef.current.master === url) {
      setNativeVariants(nativeVariantsRef.current.list);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const txt = await (await fetch(url)).text();
        const lines = txt.split('\n');
        const list = [];
        for (let i = 0; i < lines.length; i += 1) {
          const m = lines[i].match(/#EXT-X-STREAM-INF:.*RESOLUTION=\d+x(\d+)/);
          if (!m) continue;
          let j = i + 1;
          while (j < lines.length && (!lines[j].trim() || lines[j].startsWith('#'))) j += 1;
          if (j < lines.length) list.push({ height: Number(m[1]), url: new URL(lines[j].trim(), url).toString() });
        }
        if (!cancelled) {
          nativeVariantsRef.current = { master: url, list };
          setNativeVariants(list);
        }
      } catch { if (!cancelled) setNativeVariants([]); }
    })();
    return () => { cancelled = true; };
  }, [curSeg, curUrl]);
  // The playbackRate a clip starts at (ramps re-rate live in the clock loop).
  const initialRateFor = (seg) => (seg.smap && seg.smap.ramp
    ? Math.min(16, Math.max(0.0625, speedAtSource(seg, 0)))
    : (seg.speed || 1));

  // Load the CURRENT clip into the ACTIVE element. When the boundary fast-swap
  // already put a fully-preloaded element in charge (elPrep marker matches),
  // this effect is a no-op — that's what makes transitions seamless. The slow
  // path (cold start, cross-clip seeks, URL changes) loads as before.
  useEffect(() => {
    const video = vidElsRef.current[activeIdx];
    if (!video || !curSeg || !curUrl) return undefined;

    // Native-HLS manual quality: substitute the picked rung's own playlist for
    // the master. hls.js sessions ignore this (they pick levels via the API).
    const natList = nativeVariantsRef.current.master === curUrl ? nativeVariantsRef.current.list : [];
    const natChoice = NATIVE_HLS && nativePick > 0 ? natList.find((v) => v.height === nativePick) : null;
    const srcUrl = natChoice ? natChoice.url : curUrl;

    const prep = elPrepRef.current[activeIdx];
    if (prep && prep.segId === curSeg.id && prep.inSec === (curSeg.inSec || 0) && prep.url === srcUrl && video.readyState >= 1) {
      // Prepared by the standby preloader and activated at the swap. Refresh
      // the quality-levels UI from this element's own hls instance (if any),
      // and retry play in case the swap-time play() was rejected.
      const h = hlsRefs.current[activeIdx];
      setLevels(h ? h.levels || [] : []);
      if (!h) setActiveLevel(-1);
      if (playingRef.current && video.paused) video.play().catch(() => {});
      return undefined;
    }

    // Slow path: (re)load this element. Drop any hls instance it carried.
    const oldHls = hlsRefs.current[activeIdx];
    if (oldHls) {
      try { oldHls.destroy(); } catch { /* noop */ }
      hlsRefs.current[activeIdx] = null;
    }
    elPrepRef.current[activeIdx] = null;

    const onMeta = () => {
      // Never measure a window file (SDK preview) as the source duration.
      if ((!curSeg.outSec || curSeg.outSec <= 0) && !curSeg.srcOff && Number.isFinite(video.duration)) {
        setMeasured((m) => (m[curSeg.id] ? m : { ...m, [curSeg.id]: video.duration }));
      }
      // Land the clip where the movie clock actually is, not blindly at its
      // in-point. During the cross-cut load gap the clock bridges forward on
      // wall-clock so the songs keep flowing; if the video then re-locked at
      // srcPos 0 the derived time would jump BACKWARD by the gap and yank the
      // song audio (the transition blip). Starting at the clock-derived source
      // position keeps the master clock monotonic across the cut.
      const tlPos = Math.max(0, Math.min(lastTimeRef.current - curSeg.startInMovie, curSeg.len || 0));
      const srcPos = timelineToSrc(curSeg.smap, tlPos);
      try {
        video.currentTime = Math.max(0, (curSeg.inSec || 0) - (curSeg.srcOff || 0) + srcPos);
      } catch {
        /* noop */
      }
      try {
        // Ramp clips start at the local key speed; the clock loop ramps it live.
        video.playbackRate = curSeg.smap && curSeg.smap.ramp
          ? Math.min(16, Math.max(0.0625, speedAtSource(curSeg, srcPos)))
          : (curSeg.speed || 1);
      } catch {
        /* noop */
      }
      // The video now holds this clip at its in-point — let the clock derive
      // movie time from it again (ends the cross-cut wall-clock bridge).
      elPrepRef.current[activeIdx] = { segId: curSeg.id, url: srcUrl, inSec: curSeg.inSec || 0 };
      activeSegRef.current = curSeg.id;
      if (playingRef.current) {
        const wasInitialResume = initialResumeRef.current;
        video.play().catch((e) => {
          // Autoplay policy refused the play — fall back to a paused transport
          // rather than a "playing" UI over a frozen frame. NotAllowedError is
          // ALWAYS the policy (never a transient load abort), so it's safe to
          // pause on it mid-session too — one tap resumes with a fresh gesture.
          if (wasInitialResume || (e && e.name === 'NotAllowedError')) setPlaying(false);
        });
      }
      initialResumeRef.current = false;
    };
    video.addEventListener('loadedmetadata', onMeta);

    // A blob (fully-prefetched edit MP4) or any .mp4 is progressive, even if the
    // clip is tagged streamType:'hls' (RED). Only true .m3u8 takes the HLS path.
    const curIsLocal = (curUrl || '').startsWith('blob:') || /\.mp4(\?|$)/.test(curUrl || '');
    const isHls = !curIsLocal && (curSeg.streamType === 'hls' || /\.m3u8(\?|$)/.test(curUrl));
    // Forward buffer (media seconds) for edit mode, scaled by clip speed so a
    // fast clip still keeps ~18 s of wall-clock playback queued: 18 × speed,
    // clamped so normal clips don't over-buffer and fast ones don't go absurd.
    const editFwdBuffer = Math.min(240, Math.max(30, Math.round(18 * (curSeg.maxSpeed || curSeg.speed || 1))));
    let hls;
    if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      // Tuned for VOD hot-loading: prefetch well ahead, cap to the on-screen
      // size so we never pull 1080p into a small frame, keep a back buffer for
      // smooth re-seeks.
      hls = editing
        ? new Hls({
            // Editing: favor responsiveness over fidelity. Start at the lowest
            // rendition and keep a low bandwidth estimate so ABR doesn't jump to
            // a heavy rung. The edit proxy is light (~1.4 Mbps), so we can hold a
            // generous FORWARD buffer — clips played at high speed (e.g. real-
            // motion 6.67× or 10×) drain the buffer that many times faster, so we
            // scale the forward buffer with the clip's speed to keep ~18 s of
            // wall-clock queued ahead. That lets Chrome sustain high playbackRate
            // smoothly instead of stall→rebuffer (which reads as wrong speed).
            // The BACK buffer stays small to keep re-seeks snappy and memory low.
            enableWorker: true,
            lowLatencyMode: false,
            capLevelToPlayerSize: true,
            startLevel: 0,
            abrEwmaDefaultEstimate: 1_500_000,
            maxBufferLength: editFwdBuffer,
            maxMaxBufferLength: Math.min(600, editFwdBuffer * 2),
            backBufferLength: 8,
            startFragPrefetch: true,
          })
        : new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            capLevelToPlayerSize: true,
            // Published rungs are heavy (native-res bakes reach a 25 Mbps 2160p
            // rung), so ABR must EARN its way up, not assume it. The old
            // 50 Mbps default estimate started fullscreen viewers on the top
            // rung and anyone below ~10 Mbps stall-looped every few seconds
            // before ABR could learn better. Start from a modest estimate
            // (first fragments ~720p, sharpens within seconds on fast pipes)
            // and make up-switches conservative on measured bandwidth.
            startLevel: -1,
            abrEwmaDefaultEstimate: 6_000_000,
            abrBandWidthFactor: 0.8,
            abrBandWidthUpFactor: 0.6,
            // Deep prebuffer: keep minutes queued ahead so a marginal
            // connection can PAUSE AND LET IT FILL instead of rebuffering
            // every few seconds. VOD only — memory is bounded by maxBufferSize.
            maxBufferLength: 120,
            maxMaxBufferLength: 600,
            maxBufferSize: 300 * 1000 * 1000,
            backBufferLength: 30,
            startFragPrefetch: true,
          });
      hlsRefs.current[activeIdx] = hls;
      hls.loadSource(curUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLevels(hls.levels || []);
        // Re-apply the viewer's choice across clip/source changes. While editing
        // with no manual pick, force the lowest rung for fast scrubbing.
        hls.currentLevel = editing && pickedLevel === -1 ? 0 : pickedLevel;
        if (playingRef.current) video.play().catch(() => {});
      });
      hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => setActiveLevel(data.level));
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      });
    } else {
      // Native HLS (Safari) or progressive MP4: single source. Quality control
      // happens by URL substitution (srcUrl above), not a level API.
      setLevels([]);
      setActiveLevel(-1);
      video.src = srcUrl;
      video.load();
    }
    const idx = activeIdx;
    const hlsSlots = hlsRefs.current;
    return () => {
      video.removeEventListener('loadedmetadata', onMeta);
      // Only tear down the hls instance when THIS element is reloading in
      // place (cross-clip seek / URL change). After a boundary fast-swap this
      // element became the standby — destroying its instance here would blank
      // its last frame under the incoming element before it paints; the
      // standby preloader destroys it right before re-prepping instead.
      if (hls && idx === activeIdxRef.current) {
        hls.destroy();
        if (hlsSlots[idx] === hls) hlsSlots[idx] = null;
      }
    };
    // pickedLevel is applied imperatively below; excluded to avoid reloads.
    // nativePick IS a dep: switching rungs on the native player is a reload in
    // place (onMeta re-lands at the clock-derived position, so no time jump).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curSeg, curUrl, activeIdx, nativePick]);

  // Preload the NEXT clip into the STANDBY element while the current clip
  // plays: src set, metadata loaded, seeked to its in-point, buffer filling.
  // At the boundary the advance handler swaps elements instead of reloading.
  // The standby's events are never wired to the clock / buffer gate, so its
  // seek/waiting activity can't disturb playback or song sync.
  useEffect(() => {
    // Touch devices skip the standby double-buffer entirely: iOS's autoplay
    // policy doesn't reliably let the SECOND element start at a clip boundary
    // (its gesture blessing doesn't survive a src change), which froze the
    // movie at the first cut. Clip advances reload in place on the one
    // gesture-blessed element instead (the standard iOS playlist pattern) —
    // see the matching !IS_TOUCH gate on the boundary fast-swap.
    if (IS_TOUCH) return undefined;
    // Hold off while a swap is mid-flip: re-prepping the OUTGOING element
    // before the incoming one has painted would blank the visible frame.
    if (visibleIdx !== activeIdx) return undefined;
    const sIdx = 1 - activeIdx;
    const sv = vidElsRef.current[sIdx];
    if (!sv) return undefined;
    const nseg = segments[segIndex + 1];
    const nurl = nseg ? urls[nseg.id] : '';
    if (!nseg || !nurl) {
      elPrepRef.current[sIdx] = null;
      return undefined;
    }
    const prep = elPrepRef.current[sIdx];
    if (prep && prep.segId === nseg.id && prep.url === nurl && prep.inSec === (nseg.inSec || 0)) {
      return undefined;
    }

    // Re-prep: drop whatever the element held (possibly the old active hls).
    elPrepRef.current[sIdx] = null;
    const oldHls = hlsRefs.current[sIdx];
    if (oldHls) {
      try { oldHls.destroy(); } catch { /* noop */ }
      hlsRefs.current[sIdx] = null;
    }
    try { sv.pause(); } catch { /* noop */ }
    sv.muted = true;

    const onMeta = () => {
      if ((!nseg.outSec || nseg.outSec <= 0) && !nseg.srcOff && Number.isFinite(sv.duration)) {
        setMeasured((m) => (m[nseg.id] ? m : { ...m, [nseg.id]: sv.duration }));
      }
      try { sv.currentTime = Math.max(0, (nseg.inSec || 0) - (nseg.srcOff || 0)); } catch { /* noop */ }
      try { sv.playbackRate = initialRateFor(nseg); } catch { /* noop */ }
      elPrepRef.current[sIdx] = { segId: nseg.id, url: nurl, inSec: nseg.inSec || 0 };
    };
    sv.addEventListener('loadedmetadata', onMeta);

    const isLocal = nurl.startsWith('blob:') || /\.mp4(\?|$)/.test(nurl);
    const isHls = !isLocal && (nseg.streamType === 'hls' || /\.m3u8(\?|$)/.test(nurl));
    if (isHls && !sv.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      // Modest forward buffer while on standby (don't starve the active clip);
      // raised to the full playback budget at the swap.
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        capLevelToPlayerSize: true,
        startLevel: editing ? 0 : -1,
        abrEwmaDefaultEstimate: editing ? 1_500_000 : 50_000_000,
        maxBufferLength: 12,
        maxMaxBufferLength: 30,
        backBufferLength: 8,
        startFragPrefetch: true,
      });
      hlsRefs.current[sIdx] = hls;
      hls.loadSource(nurl);
      hls.attachMedia(sv);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        hls.currentLevel = editing && pickedLevel === -1 ? 0 : pickedLevel;
      });
      hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
        // Only the ACTIVE element's instance drives the quality readout.
        if (hlsRefs.current[activeIdxRef.current] === hls) setActiveLevel(data.level);
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      });
    } else {
      sv.src = nurl;
      sv.load();
    }
    return () => {
      // Listener only — never tear down media here: on a swap this element
      // just became ACTIVE and must keep playing untouched.
      sv.removeEventListener('loadedmetadata', onMeta);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segIndex, segments, urls, activeIdx, visibleIdx, editing]);

  // Apply a manual quality choice (or Auto) without tearing down the stream.
  useEffect(() => {
    const hls = hlsRefs.current[activeIdx];
    if (!hls) return;
    hls.currentLevel = editing && pickedLevel === -1 ? 0 : pickedLevel; // -1 => ABR auto
  }, [pickedLevel, editing, activeIdx]);

  // Clip advance. At the boundary, if the standby element holds the next clip
  // fully preloaded, SWAP elements (seamless: no src reload, no seek, no
  // buffer gate). Otherwise fall back to reloading in place, where the clock's
  // wall-clock bridge + slew keep the soundtrack smooth.
  useEffect(() => {
    const video = vidElsRef.current[activeIdx];
    if (!video || !curSeg) return undefined;
    const advance = () => {
      const nseg = segments[segIndex + 1];
      const sIdx = 1 - activeIdxRef.current;
      const sv = vidElsRef.current[sIdx];
      const prep = elPrepRef.current[sIdx];
      const canSwap = !IS_TOUCH && !nativeFsRef.current && !!(sv && nseg && prep && prep.segId === nseg.id
        && prep.inSec === (nseg.inSec || 0) && sv.readyState >= 3);
      adbg(canSwap ? 'boundary: fast element swap' : 'boundary: slow reload (standby not ready)', {
        next: nseg && nseg.id,
        standbyReady: sv ? sv.readyState : -1,
      });
      if (canSwap) {
        const old = video;
        // Take over the clock IMMEDIATELY (no React round-trip): the standby
        // already holds the next clip at its in-point, buffered and decoded.
        activeSegRef.current = nseg.id;
        activeIdxRef.current = sIdx;
        try { sv.volume = old.volume; } catch { /* noop */ }
        try { sv.muted = false; } catch { /* noop */ }
        try { sv.playbackRate = initialRateFor(nseg); } catch { /* noop */ }
        // Grow the standby-sized hls buffer budget to full playback size.
        const h = hlsRefs.current[sIdx];
        if (h) {
          try {
            h.config.maxBufferLength = 30;
            h.config.maxMaxBufferLength = 120;
          } catch { /* noop */ }
        }
        if (playingRef.current) sv.play().catch(() => {});
        // Reveal the incoming element only once it has PAINTED a frame, so
        // the cut is a single-frame flip — never a black flash. The outgoing
        // element keeps its last frame until then, then pauses.
        let flipped = false;
        const flip = () => {
          if (flipped) return;
          flipped = true;
          setVisibleIdx(sIdx);
          try { old.pause(); } catch { /* noop */ }
        };
        if (typeof sv.requestVideoFrameCallback === 'function') {
          sv.requestVideoFrameCallback(() => flip());
        } else {
          const onFirst = () => {
            sv.removeEventListener('timeupdate', onFirst);
            flip();
          };
          sv.addEventListener('timeupdate', onFirst);
        }
        // Safety net: never leave the old frame stuck — but only force the
        // flip once the incoming element actually has a decodable frame
        // (flipping to an unpainted element would flash black).
        const safety = () => {
          if (flipped) return;
          if (sv.readyState >= 2) flip();
          else setTimeout(safety, 250);
        };
        setTimeout(safety, 250);
        setActiveIdx(sIdx);
      }
      setSegIndex(segIndex + 1);
    };
    const onTime = () => {
      // Ignore stale events from the element we just swapped away from.
      if (video !== vidElsRef.current[activeIdxRef.current]) return;
      const outEff = curSeg.outEff && curSeg.outEff > 0 ? curSeg.outEff : Infinity;
      if (video.currentTime + (curSeg.srcOff || 0) >= outEff - 0.05) {
        if (segIndex < segments.length - 1) advance();
        else {
          video.pause();
          setPlaying(false);
        }
      }
    };
    const onEnded = () => {
      if (video !== vidElsRef.current[activeIdxRef.current]) return;
      if (segIndex < segments.length - 1) advance();
      else setPlaying(false);
    };
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curSeg, segIndex, segments, activeIdx]);

  // Detect stalls and open the buffer gate. We pause so we (not the browser)
  // decide when to resume — only once enough is buffered ahead. ACTIVE element
  // only: the standby's preload seeks/waits must never trip the gate.
  useEffect(() => {
    const video = vidElsRef.current[activeIdx];
    if (!video) return undefined;
    const isStale = () => video !== vidElsRef.current[activeIdxRef.current];
    const onWaiting = () => {
      if (playingRef.current && !isStale()) {
        adbg('video waiting -> buffer gate (video pauses; songs keep rolling <400ms)');
        setBuffering(true);
        video.pause();
      }
    };
    const onSeeking = () => {
      if (playingRef.current && !isStale()) setBuffering(true);
    };
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onWaiting);
    video.addEventListener('seeking', onSeeking);
    return () => {
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onWaiting);
      video.removeEventListener('seeking', onSeeking);
    };
  }, [curSeg, activeIdx]);

  // Recover an expired presigned raw-clip URL (progressive MP4 with an s3Key):
  // when a range request 403s after the 6h link expires it would otherwise hang
  // the buffer gate forever. On error or a long dead stall, re-presign and
  // resume at the same spot. CDN/HLS clips never expire, so skip them.
  useEffect(() => {
    const video = vidElsRef.current[activeIdx];
    if (!video || !curSeg) return undefined;
    const curIsLocal = (curUrl || '').startsWith('blob:') || /\.mp4(\?|$)/.test(curUrl || '');
    const isHls = !curIsLocal && (curSeg.streamType === 'hls' || (curUrl && /\.m3u8(\?|$)/.test(curUrl)));
    // srcOff: an SDK preview window must never "recover" onto the presigned
    // raw source — different file timeline AND flat-log color.
    if (isHls || !curSeg.s3Key || curSeg.srcOff) return undefined;

    let recovering = false;
    let stallTimer = null;

    const recover = async () => {
      if (recovering) return;
      recovering = true;
      const local = video.currentTime;
      const wasPlaying = playingRef.current;
      try {
        const fresh = await getAssetPlaybackUrl(curSeg.s3Key);
        if (fresh) {
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            try {
              if (Number.isFinite(local) && local > 0) video.currentTime = local;
              video.playbackRate = curSeg.speed || 1;
            } catch {
              /* noop */
            }
            setBuffering(false);
            if (wasPlaying) video.play().catch(() => {});
          };
          video.addEventListener('loadedmetadata', onMeta);
          video.src = fresh;
          video.load();
        }
      } catch {
        /* surface the native error if re-signing also fails */
      } finally {
        setTimeout(() => {
          recovering = false;
        }, 5000);
      }
    };

    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        if (!video.ended) recover();
      }, 15000);
    };
    const onError = () => recover();

    video.addEventListener('error', onError);
    video.addEventListener('stalled', armStall);
    video.addEventListener('waiting', armStall);
    video.addEventListener('playing', clearStall);
    video.addEventListener('timeupdate', clearStall);
    video.addEventListener('canplay', clearStall);
    return () => {
      clearStall();
      video.removeEventListener('error', onError);
      video.removeEventListener('stalled', armStall);
      video.removeEventListener('waiting', armStall);
      video.removeEventListener('playing', clearStall);
      video.removeEventListener('timeupdate', clearStall);
      video.removeEventListener('canplay', clearStall);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curSeg, curUrl, activeIdx]);

  // While buffering, poll the buffered range on a timer (the native `progress`
  // event doesn't fire for MSE/hls.js, and `timeupdate` is silent while paused).
  // This drives the live % + a fill-rate ETA, and resumes once the gate target
  // is reached.
  useEffect(() => {
    if (!buffering) {
      setBufferEta(null);
      return undefined;
    }
    const video = vidElsRef.current[activeIdx];
    if (!video) return undefined;
    const TARGET = 4; // seconds buffered ahead before we (re)start playback
    const aheadSec = () => {
      const t = video.currentTime;
      const b = video.buffered;
      for (let i = 0; i < b.length; i += 1) {
        if (t >= b.start(i) - 0.25 && t <= b.end(i)) return b.end(i) - t;
      }
      return 0;
    };

    let lastMs = 0;
    let lastAhead = aheadSec();
    let rate = 0; // EWMA of buffer-seconds gained per wall-second

    const tick = () => {
      const a = aheadSec();
      setBufferPct(Math.min(100, Math.round((a / TARGET) * 100)));

      const nowMs = performance.now();
      if (lastMs) {
        const dt = (nowMs - lastMs) / 1000;
        if (dt > 0) {
          const inst = Math.max(0, (a - lastAhead) / dt);
          rate = rate ? rate * 0.6 + inst * 0.4 : inst;
        }
      }
      lastMs = nowMs;
      lastAhead = a;

      const remaining = Math.max(0, TARGET - a);
      if (remaining <= 0) setBufferEta(0);
      else if (rate > 0.05) setBufferEta(Math.ceil(remaining / rate));
      else setBufferEta(null); // still measuring / not filling yet

      if (a >= TARGET || video.readyState >= 4) {
        setBuffering(false);
        if (playingRef.current) {
          video.play().catch((e) => {
            if (e && e.name === 'NotAllowedError') setPlaying(false);
          });
        }
      }
    };

    tick();
    const interval = setInterval(tick, 250);
    return () => clearInterval(interval);
  }, [buffering, curSeg, activeIdx]);

  // Master clock: drives the shared clockRef + original-audio ducking +
  // fade-to-black overlay. One persistent rAF.
  useEffect(() => {
    let raf;
    let lastUi = 0;
    const loop = (ts) => {
      const video = vidElsRef.current[activeIdxRef.current];
      const seg = segments[segRef.current];
      // Micro-gaps at clip switches keep the clock (and songs) rolling; only a
      // debounced, genuine stall freezes it. See stalledRef above.
      const goingNow = playingRef.current && !stalledRef.current;
      // The clip the <video> currently holds matches the active segment only
      // once it has loaded + seeked. During the load gap after a cut/seek the
      // pair would be inconsistent, so bridge with wall-clock instead so the
      // soundtrack keeps flowing forward (no backward seek / 1-second replay).
      const ready = !!(video && seg && activeSegRef.current === seg.id && video.readyState >= 1);
      // Even with the right clip loaded, the video may not be PROGRESSING yet
      // (seek settling right after a switch, buffer-gate pause, play() start
      // latency). Deriving time from a frozen currentTime would FREEZE the
      // clock while the songs keep playing — they'd drift ahead a little at
      // every cut, accumulate (nothing pulls it back), and eventually cross
      // the resync threshold: an audible yank several cuts later. Timeline
      // time always advances at wall-clock rate while playing, so bridge
      // through any freeze and let the slew below re-lock gently.
      const advancing = !!video && !video.paused && !video.seeking && video.readyState >= 3;
      let time;
      if (ready && (advancing || !goingNow)) {
        const srcPos = Math.max(0, video.currentTime + (seg.srcOff || 0) - (seg.inSec || 0));
        // Variable-speed clips ramp the <video> playbackRate live as the source
        // position crosses the speed keys, so the preview matches the bake.
        if (seg.smap && seg.smap.ramp && goingNow) {
          const sp = Math.min(16, Math.max(0.0625, speedAtSource(seg, srcPos)));
          if (Math.abs((video.playbackRate || 1) - sp) > 0.02) {
            try { video.playbackRate = sp; } catch { /* noop */ }
          }
        }
        time = seg.startInMovie + srcToTimeline(seg.smap, srcPos);
        // Never yank the master clock BACKWARD over a small gap while playing
        // (typical right after a clip switch re-lock, when the video landed a
        // touch behind the bridged clock). Slew instead: run the clock at ~85%
        // realtime so the video catches up inaudibly instead of the songs
        // hard-seeking backward. Big gaps (real seeks/desyncs) still jump.
        const prev = lastTimeRef.current;
        if (goingNow && time < prev && prev - time < 0.5) {
          const dt = lastTsRef.current ? Math.max(0, (ts - lastTsRef.current) / 1000) : 0;
          time = Math.max(time, prev + Math.min(dt, 0.5) * 0.85);
        }
      } else {
        const dt = lastTsRef.current ? Math.max(0, (ts - lastTsRef.current) / 1000) : 0;
        time = lastTimeRef.current + (goingNow ? Math.min(dt, 0.5) : 0);
        if (seg) time = Math.min(time, seg.startInMovie + seg.len); // don't overrun the clip
      }
      lastTimeRef.current = time;
      lastTsRef.current = ts;
      const { audio: master, black: blk } = fadeToBlack(time, totalDur, intro, outro);

      // Per-clip fade to black/white + approximate cross-dissolve, driven by the
      // master clock vs the clip's LOCAL timeline time (speed-ramp aware, since
      // `time - startInMovie` is already in retimed timeline seconds and seg.len
      // comes from the clip's speed map). Rendered as an overlay div — far more
      // robust than canvas work, and identical timing to the bake's `fade`
      // filters, which also run in the segment's post-retime output time.
      let fxOpacity = 0;
      let fxColor = '#000';
      let clipGain = 1; // matching gain for the clip's own audio (afade parity)
      if (seg) {
        const local = Math.min(Math.max(0, time - seg.startInMovie), seg.len || 0);
        const fin = clipFadeIn(seg);
        if (fin && local < fin.sec) {
          const o = 1 - local / fin.sec;
          if (o > fxOpacity) { fxOpacity = o; fxColor = fin.color === 'white' ? '#fff' : '#000'; }
          clipGain = Math.min(clipGain, 1 - o);
        }
        const fout = clipFadeOut(seg);
        const toEnd = Math.max(0, (seg.len || 0) - local);
        if (fout && toEnd < fout.sec) {
          const o = 1 - toEnd / fout.sec;
          if (o > fxOpacity) { fxOpacity = o; fxColor = fout.color === 'white' ? '#fff' : '#000'; }
          clipGain = Math.min(clipGain, 1 - o);
        }
        // Cross-dissolve preview APPROXIMATION: the preview plays clips
        // sequentially in ONE <video>, so a true two-video crossfade isn't
        // possible here. Instead we fade THROUGH half-black across the
        // boundary — the outgoing clip dims to 50% over the last d/2 seconds,
        // the incoming brightens from 50% over its first d/2 — which reads as
        // "a transition happens here". The bake (render.js xfade) is the real
        // dissolve; note its overlap also makes the published cut d seconds
        // shorter per transition than this hard-cut preview timeline.
        const idx = segRef.current;
        const dNext = segments[idx + 1] ? Math.min(Math.max(0, seg.dissolveSec || 0), 3) : 0;
        if (dNext > 0 && toEnd < dNext / 2) {
          fxOpacity = Math.max(fxOpacity, 0.5 * (1 - toEnd / (dNext / 2)));
        }
        const prevSeg = idx > 0 ? segments[idx - 1] : null;
        const dPrev = prevSeg ? Math.min(Math.max(0, prevSeg.dissolveSec || 0), 3) : 0;
        if (dPrev > 0 && local < dPrev / 2) {
          fxOpacity = Math.max(fxOpacity, 0.5 * (1 - local / (dPrev / 2)));
        }
      }

      // Original-audio bed: present in gaps between songs (composed), or full
      // (original mode). Ducked by the loudest covering song.
      let frac = 0;
      if (mode === 'composed') {
        for (const s of songs) {
          const vol = s.volume == null ? 1 : s.volume;
          if (vol > 0) frac = Math.max(frac, songGainAt(s, time, totalDur) / vol);
        }
      }
      // `clipGain` folds the per-clip fade into the clip's OWN audio only —
      // song lanes keep their own fade handling (they read `master`, which
      // carries just the movie-level intro/outro).
      const origGain =
        (mode === 'original' ? master : hasOriginalAudio ? Math.max(0, 1 - frac) * master : 0) * clipGain;
      if (video) {
        // iOS treats video.volume as READ-ONLY (always 1) — without the muted
        // flag, clip audio would blast at full volume over the soundtrack on
        // phones. Desktop still gets the smooth volume ramp; muted only kicks
        // in when the gain is effectively zero anyway.
        video.muted = origGain <= 0.001;
        video.volume = Math.max(0, Math.min(1, origGain));
      }
      if (goingNow && Math.abs(origGain - lastOrigGainRef.current) > 0.25) {
        adbg('clip-audio gain JUMP', {
          from: Math.round(lastOrigGainRef.current * 100) / 100,
          to: Math.round(origGain * 100) / 100,
          t: Math.round(time * 100) / 100,
        });
      }
      lastOrigGainRef.current = origGain;

      clockRef.current = { time, playing: goingNow, master, mode, dur: totalDur, epoch: seekEpochRef.current };

      if (ts - lastUi > 80) {
        lastUi = ts;
        setMovieTime(time);
        setBlack(blk);
        const op = Math.round(fxOpacity * 100) / 100;
        setFx((p) => (p.opacity === op && p.color === fxColor ? p : { color: fxColor, opacity: op }));
        if (onTime) onTime(time);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [segments, totalDur, intro, outro, mode, songs, hasOriginalAudio, onTime]);

  // Fullscreen the whole player (video + custom transport) so controls stay
  // visible. iPhone Safari has NO element fullscreen at all — there the only
  // option is the <video>'s own NATIVE fullscreen (webkitEnterFullscreen),
  // which uses the system player UI. While native fullscreen is up the
  // double-buffer boundary swap must be suppressed (the fullscreened element
  // would keep showing its frozen last frame while the hidden standby plays),
  // so clip cuts fall back to reloading in place on the same element.
  useEffect(() => {
    const onFs = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  // Track the iOS NATIVE video fullscreen lifecycle on both elements. On exit
  // iOS pauses the video — mirror that into the transport so the UI doesn't
  // show "playing" over a frozen frame.
  useEffect(() => {
    const els = vidElsRef.current.filter(Boolean);
    const onBegin = () => {
      nativeFsRef.current = true;
      setIsFs(true);
    };
    const onEnd = () => {
      nativeFsRef.current = false;
      setIsFs(false);
      const v = vidElsRef.current[activeIdxRef.current];
      if (v && v.paused && playingRef.current) setPlaying(false);
    };
    els.forEach((el) => {
      el.addEventListener('webkitbeginfullscreen', onBegin);
      el.addEventListener('webkitendfullscreen', onEnd);
    });
    return () => {
      els.forEach((el) => {
        el.removeEventListener('webkitbeginfullscreen', onBegin);
        el.removeEventListener('webkitendfullscreen', onEnd);
      });
    };
  }, []);

  const enterNativeVideoFs = (video) => {
    if (!video || typeof video.webkitEnterFullscreen !== 'function') return;
    try {
      if (video.readyState < 1) {
        // iOS defers loading until a gesture — kick the load now and enter as
        // soon as metadata lands (webkitEnterFullscreen throws before that).
        const once = () => {
          video.removeEventListener('loadedmetadata', once);
          try { video.webkitEnterFullscreen(); } catch { /* noop */ }
        };
        video.addEventListener('loadedmetadata', once);
        video.load();
        return;
      }
      video.webkitEnterFullscreen();
    } catch { /* noop */ }
  };

  const toggleFullscreen = () => {
    const root = rootRef.current;
    const video = vidElsRef.current[activeIdxRef.current];
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      return;
    }
    if (root && root.requestFullscreen) {
      // Element fullscreen where supported; fall back to the native video
      // player if the request is rejected (e.g. iPadOS quirks).
      root.requestFullscreen().catch(() => enterNativeVideoFs(video));
      return;
    }
    if (root && root.webkitRequestFullscreen) {
      try { root.webkitRequestFullscreen(); return; } catch { /* fall through */ }
    }
    enterNativeVideoFs(video); // iPhone: native video fullscreen only
  };

  const togglePlay = () => {
    const video = vidElsRef.current[activeIdxRef.current];
    if (!video) return;
    if (video.paused) {
      setPlaying(true);
      // Cold start with little data buffered: show the gate immediately and
      // hold the soundtrack too (no 400ms debounce — nothing was rolling yet,
      // so starting the song for a beat and pausing it would itself blip).
      if (video.readyState < 3) {
        setBuffering(true);
        stalledRef.current = true;
      }
      video.play().catch(() => {});
      blessedRef.current[activeIdxRef.current] = true;
      // Bless the song <audio> elements inside this gesture as well — their
      // playback is driven programmatically from the clock loop, which iOS
      // blocks per-element until each has played once in a user gesture.
      try { window.dispatchEvent(new Event('cs:prime-audio')); } catch { /* noop */ }
      // Prime the STANDBY element inside this user gesture too: Safari/iOS
      // gate programmatic play() per-element, and the boundary swap calls
      // play() on the second element outside any gesture. A muted play+pause
      // now marks it user-activated so the swap is allowed later. (Touch
      // devices never use the standby element — see the preload effect.)
      const sIdx = 1 - activeIdxRef.current;
      const sv = vidElsRef.current[sIdx];
      if (!IS_TOUCH && sv && !blessedRef.current[sIdx]) {
        blessedRef.current[sIdx] = true;
        sv.muted = true;
        const p = sv.play();
        if (p && p.then) {
          p.then(() => {
            // Only pause it if it's still on standby (not swapped in already).
            if (sIdx !== activeIdxRef.current) {
              try { sv.pause(); } catch { /* noop */ }
            }
          }).catch(() => {});
        }
      }
    } else {
      setPlaying(false);
      setBuffering(false);
      video.pause();
    }
  };

  const pauseCtl = useCallback(() => {
    const v = vidElsRef.current[activeIdxRef.current];
    if (v) v.pause();
    setPlaying(false);
    setBuffering(false);
  }, []);

  // Report playing state up so the shared timeline transport can mirror it.
  useEffect(() => {
    if (onPlayingChange) onPlayingChange(playing);
  }, [playing, onPlayingChange]);

  const seekMovie = useCallback(
    (t) => {
      const target = Math.max(0, Math.min(t, totalDur));
      let idx = segments.findIndex((s) => target < s.startInMovie + s.len);
      if (idx < 0) idx = segments.length - 1;
      const seg = segments[idx];
      if (!seg) return;
      // srcOff: SDK preview windows start at source second startSec (file t=0).
      const local = Math.max(0, (seg.inSec || 0) - (seg.srcOff || 0) + timelineToSrc(seg.smap, target - seg.startInMovie));
      setMovieTime(target);
      // Pin the soundtrack clock to the seek target so the brief reload gap on a
      // cross-clip seek holds this position instead of drifting/replaying.
      lastTimeRef.current = target;
      lastTsRef.current = 0;
      // Explicit user seek: tell the song elements to resync immediately
      // (bypassing the sustained-drift guard that exists for clip-switch jitter).
      seekEpochRef.current += 1;
      if (idx !== segRef.current) {
        // Cross-clip seek: the ACTIVE element reloads the target clip (the
        // standby prebuffer is for sequential playback only; the preload
        // effect re-preps it for the new next clip automatically).
        setSegIndex(idx);
        const v = vidElsRef.current[activeIdxRef.current];
        const onceMeta = () => {
          try {
            v.currentTime = local;
          } catch {
            /* noop */
          }
          v.removeEventListener('loadedmetadata', onceMeta);
        };
        v?.addEventListener('loadedmetadata', onceMeta);
      } else {
        const v = vidElsRef.current[activeIdxRef.current];
        if (v) {
          try {
            v.currentTime = local;
          } catch {
            /* noop */
          }
        }
      }
    },
    [segments, totalDur]
  );

  // Expose seeking to the parent (shared timeline click-to-seek).
  useEffect(() => {
    if (!seekRef) return undefined;
    seekRef.current = seekMovie;
    return () => {
      if (seekRef) seekRef.current = null;
    };
  }, [seekRef, seekMovie]);

  // Expose play/pause control to the shared timeline transport.
  useEffect(() => {
    if (!ctlRef) return undefined;
    ctlRef.current = { toggle: togglePlay, pause: pauseCtl };
    return () => { if (ctlRef) ctlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctlRef, pauseCtl]);

  const nowPlaying = useMemo(() => {
    const active = songs.filter((s) => songGainAt(s, movieTime, totalDur) > 0.001).map((s) => s.label);
    if (mode === 'original') return 'original audio';
    if (active.length) return active.join(' + ');
    return hasOriginalAudio ? 'original audio' : 'silence';
  }, [songs, movieTime, mode, hasOriginalAudio, totalDur]);

  const soundtrackToggle = hasSongs && hasOriginalAudio;

  if (segments.length === 0) return <div className="cs-empty">No clips in this movie yet.</div>;

  return (
    <div className="cs-playerroot" ref={rootRef} style={{ position: 'relative' }}>
      <div className="cs-playvid" style={{ position: 'relative' }}>
        {/* Double-buffered pair: element 0 sits in normal flow (defines the
            layout box), element 1 is stacked on top. Only `visibleIdx` shows;
            the other preloads the next clip, hidden but keeping its layout. */}
        <video
          ref={(el) => { vidElsRef.current[0] = el; }}
          className="cs-player"
          playsInline
          preload="auto"
          poster={poster}
          onClick={togglePlay}
          style={{ visibility: visibleIdx === 0 ? 'visible' : 'hidden' }}
        />
        <video
          ref={(el) => { vidElsRef.current[1] = el; }}
          className="cs-player"
          playsInline
          preload="auto"
          muted
          onClick={togglePlay}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            maxHeight: 'none',
            aspectRatio: 'auto',
            objectFit: 'contain',
            visibility: visibleIdx === 1 ? 'visible' : 'hidden',
          }}
        />
        {/* Per-clip fade / dissolve-approximation overlay (under the movie fade). */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: fx.color,
            opacity: fx.opacity,
            pointerEvents: 'none',
            transition: 'opacity 0.09s linear',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: '#000',
            opacity: black,
            pointerEvents: 'none',
            transition: 'opacity 0.05s linear',
          }}
        />
        {buffering && (
          <div className="cs-buffering">
            <div className="cs-spinner" />
            <div className="cs-buffering-pct">Buffering {bufferPct}%</div>
            <div className="cs-buffering-eta">
              {bufferEta == null
                ? 'estimating…'
                : bufferEta <= 0
                ? 'ready'
                : `~${bufferEta}s to go`}
            </div>
          </div>
        )}
      </div>
      {uploadSongs.map((s) => (
        <UploadTrack key={s.id} song={s} clockRef={clockRef} />
      ))}
      {ytSongs.map((s) => (
        <YouTubeTrack key={`${s.id}:${s.videoId}`} song={s} clockRef={clockRef} />
      ))}

      <div className="cs-transport">
        <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={togglePlay}>
          {playing ? '❙❙' : '►'}
        </button>
        <span className="cs-time">{fmtTime(movieTime)}</span>
        <input
          className="cs-slider cs-scrub"
          type="range"
          min="0"
          max={Math.max(totalDur, 0.1)}
          step="0.1"
          value={Math.min(movieTime, totalDur)}
          onChange={(e) => seekMovie(Number(e.target.value))}
        />
        <span className="cs-time">{fmtTime(totalDur)}</span>
        {segments.length > 1 && (
          <span className="cs-time" title="Current clip">
            clip {segIndex + 1}/{segments.length}
          </span>
        )}
        {/* Soundtrack toggle lives inline in the transport row (was its own
            full-height bar) — tiny segmented control, now-playing in tooltip. */}
        {soundtrackToggle && (
          <div className="cs-segment cs-segment-sm" title={`Audio · now: ${nowPlaying}`}>
            <button
              className={mode === 'composed' ? 'on' : ''}
              title={`Composed soundtrack (your songs) — now: ${nowPlaying}`}
              onClick={() => setMode('composed')}
            >
              ♫ Mix
            </button>
            <button
              className={mode === 'original' ? 'on' : ''}
              title="Original clip audio"
              onClick={() => setMode('original')}
            >
              Original
            </button>
          </div>
        )}
        {levels.length > 1 ? (
          <div className="cs-quality">
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => setQualityOpen((o) => !o)}
              title="Video quality"
            >
              {pickedLevel === -1
                ? `Auto${activeLevel >= 0 && levels[activeLevel] ? ` · ${levels[activeLevel].height}p` : ''}`
                : `${levels[pickedLevel]?.height}p`}
              {' ▾'}
            </button>
            {qualityOpen && (
              <div className="cs-quality-menu">
                <button
                  className={`cs-quality-item${pickedLevel === -1 ? ' is-active' : ''}`}
                  onClick={() => {
                    setPickedLevel(-1);
                    setQualityOpen(false);
                  }}
                >
                  Auto
                </button>
                {levels
                  .map((l, i) => ({ l, i }))
                  .sort((a, b) => b.l.height - a.l.height)
                  .map(({ l, i }) => (
                    <button
                      key={i}
                      className={`cs-quality-item${pickedLevel === i ? ' is-active' : ''}`}
                      onClick={() => {
                        setPickedLevel(i);
                        setQualityOpen(false);
                      }}
                    >
                      {l.height}p
                    </button>
                  ))}
              </div>
            )}
          </div>
        ) : nativeVariants.length > 1 ? (
          <div className="cs-quality">
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => setQualityOpen((o) => !o)}
              title="Video quality — pick a lower rung if playback keeps buffering"
            >
              {nativePick === 0 ? 'Auto' : `${nativePick}p`}
              {' ▾'}
            </button>
            {qualityOpen && (
              <div className="cs-quality-menu">
                <button
                  className={`cs-quality-item${nativePick === 0 ? ' is-active' : ''}`}
                  onClick={() => {
                    setNativePick(0);
                    setQualityOpen(false);
                  }}
                >
                  Auto
                </button>
                {[...nativeVariants]
                  .sort((a, b) => b.height - a.height)
                  .map((v) => (
                    <button
                      key={v.height}
                      className={`cs-quality-item${nativePick === v.height ? ' is-active' : ''}`}
                      onClick={() => {
                        setNativePick(v.height);
                        setQualityOpen(false);
                      }}
                    >
                      {v.height}p
                    </button>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <span className="cs-time" title="Publish for adaptive quality" style={{ opacity: 0.6 }}>
            Source
          </span>
        )}
        <button
          className="cs-btn cs-btn-ghost cs-btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={toggleFullscreen}
          title={isFs ? 'Exit full screen' : 'Full screen'}
        >
          {isFs ? '⤢' : '⛶'}
        </button>
      </div>
    </div>
  );
}
